import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { configureForm } from '@thinkmill/pragmatic-forms';
import authManager, { AUTH_STATE_CHANGE } from '../../lib/authManager';
import { css } from 'glamor';
import { Container } from '../../components/Container';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { LogoBox } from '../../components/LogoBox';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles } from "@material-ui/core/styles";
import { Redirect } from "react-router-dom";
import { ValidationErrorMessage } from "../../components/ErrorMessage";
import RequestError from '../../lib/RequestError';

const formLayout = css({
    display: 'flex',
    flexDirection: 'column',
});

const useStyles = makeStyles(() => ({
    card: {
        width: '650px',
        height: '620px',
        borderRadius: '25px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'linear-gradient(#B0FF33, #97C93B)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 6px 10px rgba(0,0,0,.08), 0 0 6px rgba(0,0,0,.05)',
        transition: '.3s transform cubic-bezier(.155,1.105,.295,1.12),.3s box-shadow',
    },
    nestedCard: {
        width: '621px',
        height: '591px',
        borderRadius: '25px',
        background: '#fff',
    },
    cardContent: {
        alignItems: 'center',
    },
}));

function Login({ form }) {
    const classes = useStyles();

    const [isAuthenticated, setIsAuthenticated] = useState(authManager.isAuthenticated);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const onAuthStateChange = () => setIsAuthenticated(authManager.isAuthenticated);
        authManager.addListener("AUTH_STATE_CHANGE", onAuthStateChange);

        return () => authManager.removeListener("AUTH_STATE_CHANGE", onAuthStateChange);
    }, []);

    if (isAuthenticated) return <Redirect to="/installations" />;

    const handleSubmit = async ({ email, password }) => {
        const newErrors = {};

        if (!email?.value) newErrors.email = 'Please enter your email address';
        if (!password?.value) newErrors.password = 'Please enter your password';

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        try {
            setLoading(true);
            await authManager.login({
                email: email.value,
                password: password.value,
            });
        } catch (e) {
            console.error('Login error:', e);

            const message = e instanceof RequestError
                ? e.message
                : 'Invalid email or password. Please try again.';

            setErrors({ submit: message  });
        } finally {
            setLoading(false);
        }
    };

    return (
      <div>
          <Card className={classes.card}>
              <Card className={classes.nestedCard}>
                  <Container size="small">
                      <CardContent className={classes.cardContent}>
                          <Helmet><title>Login</title></Helmet>

                          <div className={css({ paddingTop: 40, paddingBottom: 40 })}>
                              <LogoBox size={80} src={'/DRIVO_Logo.png'} />
                          </div>

                        <form.Form className={formLayout} onSubmit={(e) => { e.preventDefault(); handleSubmit(form.fields); }}>
                              {Object.keys(errors).length > 0 &&
                                Object.values(errors).map((value) => (
                                  <ValidationErrorMessage key={value} error={{ message: value }}/>
                                ))
                              }
                              <Input
                                {...form.getFieldProps({
                                    name: 'email',
                                    type: 'email',
                                })}
                                placeholder="Email Address"
                                autoComplete="username"
                              />
                              <Input
                                style={{ marginTop: '15px' }}
                                {...form.getFieldProps({
                                    name: 'password',
                                    type: 'password',
                                })}
                                placeholder="Password"
                                autoComplete="current-password"
                              />

                              <Button
                                type="submit"
                                onClick={() => handleSubmit(form.fields)}
                                style={{ marginTop: '20px', height: '45px', fontSize: '18px' }}
                                block
                                disabled={loading}
                              >
                                  {loading ? 'Signing in…' : 'Sign in'}
                              </Button>
                          </form.Form>
                      </CardContent>
                  </Container>
              </Card>
          </Card>
      </div>
    );
}

export default configureForm({
    initFields: () => ({ email: '', password: '' }),
})(Login);
